import React, { useEffect, useState } from "react";
import { Copy, ExternalLink, QrCode, FileDown, Loader2, Mail, Activity } from "lucide-react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/admin/ui";
import { applyLink } from "./Organizations";
import { SavedMessages } from "@/components/admin/SavedMessages";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/typed";

export function EmailDialog({ title, open, onClose, send, testId, withMessage = false }: { title: string; open: boolean; onClose: () => void; send: (to: string, message: string) => Promise<any>; testId: string; withMessage?: boolean }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await send(to.trim(), message.trim());
      toast.success(`Sent to ${to.trim()}`);
      setTo("");
      setMessage("");
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={go} className="space-y-4">
          <label className="block"><span className="field-label">Send to (email address)</span>
            <input type="email" required autoFocus className="field-input" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${testId}-input`} />
          </label>
          {withMessage && (
            <label className="block"><span className="field-label">Your message (optional)</span>
              <textarea rows={5} maxLength={3000} className="field-input min-h-[120px] py-3" placeholder={"e.g. Hi Sarah, please complete your background check by Friday using the link below. Call me with any questions."} value={message} onChange={(e) => setMessage(e.target.value)} data-testid={`${testId}-message`} />
              <span className="mt-1 block text-xs text-[#94A3B8]">Shown at the top of the email, above the application link and instructions. Tap a saved message to use it.</span>
            </label>
          )}
          {withMessage && open && <SavedMessages message={message} onPick={setMessage} testId={testId} />}
          <button className="btn-primary w-full" disabled={busy} data-testid={`${testId}-send-button`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send email</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const FORMS: Record<string, string> = { standard: "Standard", dow: "Diocese of Wilmington", nch: "Short form" };

export const linkFor = (o: any) => ((o.applicant_pays || o.code_or_pay) && o.apply_slug ? applyLink(o.apply_slug) : `${window.location.origin}/apply`);

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

export async function printFlyer(o: any, url: string) {
  const w = window.open("", "_blank");
  if (!w) return toast.error("Please allow pop-ups to print the flyer.");
  const qr = await QRCode.toDataURL(url, { width: 600, margin: 1 });
  const pay = o.code_or_pay ? `Enter your entry code, or pay the $${Number(o.applicant_fee || 50).toFixed(2)} screening fee by card at the end.` : o.applicant_pays ? `Screening fee: $${Number(o.applicant_fee || 50).toFixed(2)}, paid by card at the end.` : "Have your entry code ready.";
  w.document.write(`<!doctype html><html><head><title>${esc(o.name)} - Background Check</title><style>
    body{font-family:Georgia,serif;text-align:center;padding:48px;color:#1A2536}h1{font-size:40px;margin:0 0 8px}h2{font-size:22px;font-weight:normal;margin:0 0 32px;color:#475569}
    img{width:340px;height:340px}p{font-size:18px;margin:12px 0}.url{font-family:monospace;font-size:14px;word-break:break-all;color:#334155}.brand{margin-top:40px;font-size:14px;color:#64748B}
  </style></head><body><h1>${esc(o.name)}</h1><h2>Background Check Online Application</h2><img src="${qr}" alt="QR code"/>
  <p><b>Scan with your phone camera to apply.</b></p><p>${pay}</p><p class="url">${esc(url)}</p><p class="brand">Processed by Info Retrieval Services</p>
  <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
}

export function PaperFormsPicker({ orgs, base = "/admin" }: { orgs: any[]; base?: string }) {
  const [id, setId] = useState("");
  const org = orgs.find((o) => o.id === (id || orgs[0]?.id));
  if (!org) return null;
  return (
    <div className="space-y-3">
      {orgs.length > 1 && (
        <label className="flex flex-wrap items-center gap-3"><span className="text-sm font-semibold text-[#1A2536]">Paper forms for</span>
          <select className="field-input max-w-md" value={org.id} onChange={(e) => setId(e.target.value)} data-testid="paper-form-org-select">
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      )}
      <PaperForms key={org.id} org={org} base={base} />
    </div>
  );
}

function PaperForms({ org, base }: { org: any; base: string }) {
  const [loc, setLoc] = useState("");
  const [lang, setLang] = useState<"en" | "es">("en");
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState<"one" | "all" | null>(null);
  const sfx = lang === "es" ? "-ES" : "";
  const one = () => downloadFile(`${base}/organizations/${org.id}/paper-form.pdf?location=${encodeURIComponent(loc)}&lang=${lang}`, `DOW-Paper-Form-${loc}${sfx}.pdf`).catch((e) => toast.error(errMsg(e)));
  const all = async () => {
    setBusy(true);
    await downloadFile(`${base}/organizations/${org.id}/paper-forms.zip?lang=${lang}`, `DOW-Paper-Forms${sfx}.zip`).catch((e) => toast.error(errMsg(e)));
    setBusy(false);
  };
  return (
    <Card>
      <div className="space-y-4 p-6" data-testid="paper-forms-panel">
        <div>
          <h3 className="text-lg font-bold text-[#1A2536]">Paper forms: {org.name}</h3>
          <p className="text-sm text-[#64748B]">Printable 5-page form for applicants who can't apply online, with the parish or school name printed at the top.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-xl bg-[#F1EFEA] p-1" data-testid="paper-form-language">
            {([["en", "English"], ["es", "Español"]] as const).map(([v, l]) => (
              <button key={v} type="button" onClick={() => setLang(v)} data-testid={`paper-form-lang-${v}`}
                className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${lang === v ? "bg-white text-[#1B365D] shadow-sm" : "text-[#64748B]"}`}>{l}</button>
            ))}
          </div>
          <select className="field-input max-w-sm" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="paper-form-location-select">
            <option value="">Choose a parish or school…</option>
            {(org.locations || []).map((l: string) => <option key={l}>{l}</option>)}
          </select>
          <button className="btn-secondary btn-sm" disabled={!loc} onClick={one} data-testid="paper-form-download-button"><FileDown className="h-4 w-4" /> Download PDF</button>
          <button className="btn-primary btn-sm" disabled={busy} onClick={all} data-testid="paper-form-download-all-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download all {(org.locations || []).length} (zip)
          </button>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="btn-secondary btn-sm" disabled={!loc} onClick={() => setMail("one")} data-testid="paper-form-email-button"><Mail className="h-4 w-4" /> Email selected form</button>
          <button className="btn-secondary btn-sm" onClick={() => setMail("all")} data-testid="paper-form-email-all-button"><Mail className="h-4 w-4" /> Email all forms (zip)</button>
        </div>
        <EmailDialog open={!!mail} onClose={() => setMail(null)} testId="paper-form-email"
          title={`${mail === "one" ? `Email paper form: ${loc}` : `Email all ${(org.locations || []).length} paper forms`}${lang === "es" ? " (Spanish)" : ""}`}
          send={(to) => api.post(`${base}/organizations/${org.id}/email-paper-form`, { to, location: mail === "one" ? loc : null, lang })} />
      </div>
    </Card>
  );
}

export default function OrgLinks() {
  const [orgs, setOrgs] = useState<any[]>([]);
  const [mailOrg, setMailOrg] = useState<any>(null);
  const [statusOrg, setStatusOrg] = useState<any>(null);
  useEffect(() => { api.get("/admin/organizations").then((r) => setOrgs(r.data)); }, []);
  const [showParish, setShowParish] = useState(false);
  const parishCount = orgs.filter((o) => o.parent_org_id).length;
  const shown = showParish ? orgs : orgs.filter((o) => !o.parent_org_id);
  const copy = (url: string) => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  const statusLink = async (o: any) => {
    try {
      const { data } = await api.post(`/admin/organizations/${o.id}/status-link`);
      await navigator.clipboard.writeText(data.url).catch(() => null);
      toast.success("Private status page link copied", { description: data.url });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Application Links" subtitle="Every organization's application link and fee, ready to share." />
      {parishCount > 0 && (
        <div className="mb-3 flex justify-end">
          <button className="btn-secondary btn-sm" onClick={() => setShowParish(!showParish)} data-testid="org-links-toggle-parishes">
            {showParish ? "Hide" : "Show"} {parishCount} Diocese parish / school organizations
          </button>
        </div>
      )}
      <Card>
        {orgs.length ? (
          <div className="overflow-x-auto">
            <table className="tbl" data-testid="org-links-table">
              <thead><tr><th>Organization</th><th>Form</th><th>Payment</th><th>Application link</th><th></th></tr></thead>
              <tbody>
                {shown.map((o) => {
                  const url = linkFor(o);
                  return (
                    <tr key={o.id} data-testid={`org-link-row-${o.id}`}>
                      <td className="font-semibold">{o.name}</td>
                      <td className="text-sm">{FORMS[o.form_type || "standard"]}</td>
                      <td data-testid={`org-link-fee-${o.id}`}>
                        {o.code_or_pay ? <span className="text-sm">Code or <span className="font-mono font-semibold">${Number(o.applicant_fee || 50).toFixed(2)}</span></span> : o.applicant_pays ? <span className="font-mono font-semibold">${Number(o.applicant_fee || 50).toFixed(2)}</span> : <span className="text-sm text-[#64748B]">Entry code</span>}
                      </td>
                      <td className="min-w-[260px] max-w-[360px]">
                        <span className="break-all font-mono text-xs" data-testid={`org-link-url-${o.id}`}>{url}</span>
                        {!o.applicant_pays && !o.code_or_pay && <p className="text-xs text-[#94A3B8]">Applicants enter their code here</p>}
                      </td>
                      <td className="whitespace-nowrap">
                        <button className="btn-secondary btn-sm" onClick={() => copy(url)} data-testid={`org-link-copy-${o.id}`}><Copy className="h-4 w-4" /> Copy</button>
                        <a className="btn-secondary btn-sm ml-2" href={url} target="_blank" rel="noreferrer" data-testid={`org-link-open-${o.id}`}><ExternalLink className="h-4 w-4" /></a>
                        <button className="btn-secondary btn-sm ml-2" onClick={() => printFlyer(o, url)} data-testid={`org-link-qr-${o.id}`}><QrCode className="h-4 w-4" /> QR flyer</button>
                        <button className="btn-secondary btn-sm ml-2" onClick={() => setMailOrg(o)} data-testid={`org-link-email-${o.id}`}><Mail className="h-4 w-4" /> Email</button>
                        <button className="btn-secondary btn-sm ml-2" onClick={() => statusLink(o)} data-testid={`org-status-link-${o.id}`}><Activity className="h-4 w-4" /> Status page</button>
                        <button className="btn-secondary btn-sm ml-2" onClick={() => setStatusOrg(o)} data-testid={`org-status-email-${o.id}`}><Mail className="h-4 w-4" /> Email status</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <Empty text="No organizations yet." />}
      </Card>
      <EmailDialog open={!!mailOrg} onClose={() => setMailOrg(null)} testId="org-link-email" title={`Email application link: ${mailOrg?.name || ""}`}
        withMessage send={(to, message) => api.post(`/admin/organizations/${mailOrg.id}/email-link`, { to, message })} />
      <EmailDialog open={!!statusOrg} onClose={() => setStatusOrg(null)} testId="org-status-email" title={`Email private status page: ${statusOrg?.name || ""}`}
        send={(to) => api.post(`/admin/organizations/${statusOrg.id}/email-status-link`, { to })} />
      <div className="mt-6"><PaperFormsPicker orgs={orgs.filter((o) => o.form_type === "dow" && !o.parent_org_id)} /></div>
    </div>
  );
}
